import {
  Box,
  Center,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import * as React from 'react';

import { resource_downloaded } from '@/analytics';
import { FileNameTooltipLabel } from '@/components/FileNameTooltipLabel';
import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { TruncatedItemName } from '@/components/TruncatedItemName';
import { getFileTypeDisplayInfo } from '@/imports/ui/UtilsNoVulcan';
import { Card } from '@/imports/ui/chakra/components/Card';
import { DownloadIcon, XIcon } from '@/imports/ui/chakra/feather';

import { IEventResource } from '../constants';

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer').then((mod) => mod.PreviewRenderer),
  {
    ssr: false,
  }
);

const NUM_LINES = 2;

export const ResourceCard: React.FC<{
  canUserEdit: boolean;
  isRemoving: boolean;
  onRemoveResource: ((resourceId: string) => void) | null;
  resource: IEventResource;
}> = ({ canUserEdit, isRemoving, onRemoveResource, resource }) => {
  const lineLength = useBreakpointValue({ base: 25, sm: 35, md: 20, lg: 25 });

  const previewModal = useDisclosure();

  const [resourceIdToDelete, setResourceIdToDelete] = React.useState<
    string | null
  >(null);

  const resourceFile = resource.resourceSpec.file;

  const { ChakraIcon, previewDisabled } = getFileTypeDisplayInfo(
    resourceFile.contentType
  );

  const handleDownloadClick = (): void => {
    resource_downloaded({
      entity_id: resource._id,
      entity_type: 'resource',
      resource_id: resource._id,
      resource_name: resourceFile.name,
      resource_content_type: resourceFile.contentType,
      resource_source: 'event_resource_card',
      author_user_id: resource.authorId,
    });
  };

  // Show spinner for specific card being deleted
  if (isRemoving && resource._id === resourceIdToDelete) {
    return (
      <Center h={20}>
        <Spinner />
      </Center>
    );
  }

  return (
    <Card
      px="4"
      py="4"
      _hover={{ cursor: 'pointer', bg: 'gray.50' }}
      onClick={previewModal.onOpen}
      w="100%"
      shadow="xs"
    >
      <PreviewRendererModal
        onClose={previewModal.onClose}
        isOpen={previewModal.isOpen}
        eventResource={resource}
      />
      <HStack>
        <Tooltip
          hasArrow
          label={
            <FileNameTooltipLabel
              previewDisabled={!!previewDisabled}
              fileName={resourceFile.name}
              fileNameLengthThreshold={
                !!lineLength ? lineLength * NUM_LINES : 0
              }
            />
          }
          isDisabled={
            !previewDisabled &&
            !!lineLength &&
            resourceFile.name.length <= lineLength * NUM_LINES
          }
        >
          <HStack>
            <ChakraIcon color="gray.600" boxSize={6} />
            <TruncatedItemName
              name={resourceFile.name}
              minEndChars={10}
              numOfLines={NUM_LINES}
              maxLineLength={lineLength}
            />
          </HStack>
        </Tooltip>
        <Spacer />
        <HStack>
          <Box onClick={(e) => e.stopPropagation()}>
            <NextLink
              href={resourceFile.downloadUrl}
              download={resourceFile.name}
            >
              <IconButton
                variant="ghost"
                colorScheme="gray"
                icon={<DownloadIcon />}
                aria-label={`download ${resourceFile.name}`}
                onClick={handleDownloadClick}
              />
            </NextLink>
          </Box>
          {canUserEdit && onRemoveResource && (
            <Box onClick={(e) => e.stopPropagation()}>
              <IconButton
                variant="ghost"
                colorScheme="gray"
                icon={<XIcon />}
                aria-label="Delete document"
                onClick={() => {
                  onRemoveResource(resource._id);
                  setResourceIdToDelete(resource._id);
                }}
              />
            </Box>
          )}
        </HStack>
      </HStack>
    </Card>
  );
};
